import { useState, useCallback } from "react";
import PageShell from "../../../components/PageShell";
import DataTable from "../../../components/DataTable";
import DataForm from "../../../components/DataForm";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import ConfirmDialog from "../../../components/ConfirmDialog";
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
} from "../../../components/ui/context-menu";
import {
  usePackages,
  useSavePackage,
  useArchivePackage,
} from "../hooks/usePackages";
import toast from "react-hot-toast";
import { useTranslation } from "../../../i18n";

const COLUMNS = [
  { header: "col.id", accessor: (row) => row.package?.id, type: "number" },
  {
    header: "col.minutesAmount",
    accessor: (row) => row.package?.minutesAmount,
    type: "number",
  },
  {
    header: "col.onlineBonus",
    accessor: (row) => row.package?.onlinePaymentBonus,
    type: "number",
  },
  {
    header: "col.cashBonus",
    accessor: (row) => row.package?.cachePaymentBonus,
    type: "number",
  },
  {
    header: "col.vipBonus",
    accessor: (row) => row.package?.vipBonus,
    type: "number",
  },
  {
    header: "col.price",
    accessor: (row) => row.package?.priceRub,
    type: "number",
  },
  {
    header: "col.discount",
    accessor: "discount",
    type: "percent",
    mapper: (v) => `${Number(v * 100)}%`,
  },
  {
    header: "col.isInstant",
    accessor: (row) => row.package?.isInstant,
    type: "boolean",
  },
  {
    header: "col.available",
    accessor: (row) => row.package?.available,
    type: "boolean",
  },
];

const FORM_FIELDS = [
  { name: "id", label: "col.id", type: "number", disabled: true },
  { name: "minutesAmount", label: "form.minutesAmount", type: "number" },
  {
    name: "onlinePaymentBonus",
    label: "col.onlineBonus",
    type: "number",
    required: false,
  },
  {
    name: "cachePaymentBonus",
    label: "col.cashBonus",
    type: "number",
    required: false,
  },
  {
    name: "vipBonus",
    label: "col.vipBonus",
    type: "number",
    required: false,
  },
  { name: "priceRub", label: "form.price", type: "number" },
  { name: "possibleDiscount", label: "form.discount", type: "percent" },
  {
    name: "isInstant",
    label: "form.isInstant",
    type: "boolean",
    required: false,
  },
  {
    name: "available",
    label: "form.available",
    type: "boolean",
    required: false,
    default: true,
  },
];

const CREATE_DEFAULTS = {
  minutesAmount: 0,
  priceRub: 0,
  onlinePaymentBonus: 0,
  cachePaymentBonus: 0,
  vipBonus: 0,
  isInstant: false,
  available: true,
  possibleDiscount: "0%",
};

export default function PackagesPage() {
  const { t } = useTranslation();
  const { data: packages, isLoading } = usePackages();
  const saveMutation = useSavePackage();
  const archiveMutation = useArchivePackage();
  const [editingRow, setEditingRow] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [values, setValues] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);

  const openEdit = useCallback((row) => {
    setEditingRow(row);
    const pkg = row.package || {};
    setValues({
      id: pkg.id,
      minutesAmount: pkg.minutesAmount ?? 0,
      priceRub: pkg.priceRub ?? 0,
      onlinePaymentBonus: pkg.onlinePaymentBonus ?? 0,
      cachePaymentBonus: pkg.cachePaymentBonus ?? 0,
      vipBonus: pkg.vipBonus ?? 0,
      isInstant: pkg.isInstant ?? false,
      available: pkg.available ?? true,
      possibleDiscount:
        row.discount != null ? `${Number(row.discount * 100)}%` : "0%",
    });
    setIsNew(false);
  }, []);

  const openCreate = useCallback(() => {
    setEditingRow(CREATE_DEFAULTS);
    setValues({ ...CREATE_DEFAULTS });
    setIsNew(true);
  }, []);

  const handleSave = () => {
    if (!values) return;
    const req = { ...values };
    if (isNew) {
      req.package = {
        minutesAmount: req.minutesAmount,
        priceRub: req.priceRub,
        onlinePaymentBonus: req.onlinePaymentBonus,
        cachePaymentBonus: req.cachePaymentBonus,
        vipBonus: req.vipBonus,
        isInstant: req.isInstant,
        available: req.available,
      };
      delete req.id;
    } else {
      req.id = values.id;
    }
    saveMutation.mutate(req, {
      onSuccess: () => {
        toast.success(
          isNew ? t("toast.recordCreated") : t("toast.recordUpdated"),
        );
        setEditingRow(null);
        setValues(null);
      },
      onError: () => toast.error(t("toast.recordSaveFailed")),
    });
  };

  const renderArchiveMenu = useCallback(
    (row, children) => (
      <ContextMenu>
        <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
        <ContextMenuContent>
          <ContextMenuItem
            className="bg-c-red-500 hover:bg-c-red-600 text-white"
            onClick={() => setArchiveTarget(row)}
          >
            {t("common.archive")}
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
    ),
    [],
  );

  const handleArchive = () => {
    if (!archiveTarget?.package?.id) return;
    archiveMutation.mutate(archiveTarget.package.id, {
      onSuccess: () => {
        toast.success(t("toast.packageArchived"));
        setArchiveTarget(null);
      },
      onError: () => toast.error(t("toast.packageArchiveFailed")),
    });
  };

  return (
    <PageShell
      title={t("page.packages")}
      actions={<Button onClick={openCreate}>{t("common.add")}</Button>}
    >
      {isLoading ? (
        <p className="text-c-gray-400">{t("common.loading")}</p>
      ) : (
        <DataTable
          columns={COLUMNS}
          data={packages}
          onRowClick={openEdit}
          renderContextMenu={renderArchiveMenu}
        />
      )}

      <Dialog
        open={!!editingRow}
        onOpenChange={(o) => {
          if (!o) {
            setEditingRow(null);
            setValues(null);
          }
        }}
      >
        <DialogContent className="left-auto right-0 top-0 translate-x-0 translate-y-0 h-dvh max-h-none rounded-none sm:rounded-none border-r-0 border-t-0 border-b-0 data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right min-w-[480px] grid-rows-[auto_1fr_auto]">
          <DialogHeader>
            <DialogTitle>
              {isNew ? t("dialog.addPackage") : t("dialog.editRecord")}
            </DialogTitle>
          </DialogHeader>
          <form
            id="edit-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
            className="flex-1 overflow-y-auto px-2"
          >
            <DataForm
              fields={FORM_FIELDS}
              values={values}
              onChange={setValues}
            />
          </form>
          <DialogFooter className="flex gap-2">
            <Button type="submit" form="edit-form">
              {t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!archiveTarget}
        onOpenChange={(o) => {
          if (!o) setArchiveTarget(null);
        }}
        itemName={`${t("page.packages").toLowerCase()} "${archiveTarget?.package?.minutesAmount || ""} ${t("common.minutes")}"`}
        onConfirm={handleArchive}
      />
    </PageShell>
  );
}
